import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  User,
  FileText,
  Clock,
  Leaf,
  FileDown,
  Sparkles,
  Calendar,
  Eye,
  Pill,
} from "lucide-react";
import { patientSchema, medicationSchema, allergySchema } from "@/lib/validation";
import { VitalityAssessment, PatientChild } from "@/types/vitality";
import { QuestionnairesSection } from "@/components/patient/QuestionnairesSection";
import { ChildrenSection } from "@/components/patient/ChildrenSection";
import { PatientHeader } from "@/components/patient/PatientHeader";
import { PatientQuickActions } from "@/components/patient/PatientQuickActions";
import { PatientInfoForm } from "@/components/patient/PatientInfoForm";
import { MedicationsCard } from "@/components/patient/MedicationsCard";
import { AllergiesCard } from "@/components/patient/AllergiesCard";
import { useDraftPersistence } from "@/hooks/useDraftPersistence";

interface PatientData {
  id?: string;
  name: string;
  date_of_birth: string;
  gender: string;
  phone: string;
  email: string;
  address: string;
  medical_history: string;
}

interface Medication {
  id?: string;
  medication_name: string;
  dosage: string;
  frequency: string;
  started_at: string;
}

interface Allergy {
  id?: string;
  allergen: string;
  severity: string;
}

export default function PatientFile() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [patient, setPatient] = useState<PatientData>({
    name: "",
    date_of_birth: "",
    gender: "homme",
    phone: "",
    email: "",
    address: "",
    medical_history: "",
  });

  const [medications, setMedications] = useState<Medication[]>([]);
  const [allergies, setAllergies] = useState<Allergy[]>([]);
  const [patientChildren, setPatientChildren] = useState<PatientChild[]>([]);
  const [consultations, setConsultations] = useState<any[]>([]);
  const [vitalityAssessments, setVitalityAssessments] = useState<any[]>([]);
  const [scoringAssessments, setScoringAssessments] = useState<any[]>([]);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [sessionReports, setSessionReports] = useState<any[]>([]);
  const [iridologySessions, setIridologySessions] = useState<any[]>([]);

  // Quick view dialogs
  const [selectedVitality, setSelectedVitality] = useState<any | null>(null);
  const [selectedConsultation, setSelectedConsultation] = useState<any | null>(null);
  const [selectedSessionReport, setSelectedSessionReport] = useState<any | null>(null);
  const [selectedIridology, setSelectedIridology] = useState<any | null>(null);

  // Persistent local draft — protects against network failures / browser crashes
  const draftKey = id ? `patient:${id}` : null;
  const { storedDraft, clearDraft } = useDraftPersistence(
    draftKey,
    { patient, medications, allergies },
    { delayMs: 1500 }
  );

  useEffect(() => {
    if (id && id !== "new") {
      loadPatient(id);
    }
  }, [id]);

  // Offer to restore unsaved draft if it's newer than the server data
  useEffect(() => {
    if (!storedDraft || loading) return;
    const draftAge = Date.now() - new Date(storedDraft.savedAt).getTime();
    // Only offer if draft is less than 7 days old and differs from current state
    if (draftAge > 7 * 24 * 3600 * 1000) {
      clearDraft();
      return;
    }
    const sameMeds =
      JSON.stringify(storedDraft.data.medications) === JSON.stringify(medications);
    const sameAllergies =
      JSON.stringify(storedDraft.data.allergies) === JSON.stringify(allergies);
    const samePatient =
      JSON.stringify(storedDraft.data.patient) === JSON.stringify(patient);
    if (sameMeds && sameAllergies && samePatient) return;

    toast("Brouillon non enregistré détecté", {
      description: `Modifications du ${new Date(storedDraft.savedAt).toLocaleString("fr-FR")}. Restaurer ?`,
      duration: 15000,
      action: {
        label: "Restaurer",
        onClick: () => {
          setPatient(storedDraft.data.patient);
          setMedications(storedDraft.data.medications);
          setAllergies(storedDraft.data.allergies);
          toast.success("Brouillon restauré");
        },
      },
      cancel: {
        label: "Ignorer",
        onClick: () => clearDraft(),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const loadPatient = async (patientId: string) => {
    setLoading(true);
    try {
      const { data: patientData, error: patientError } = await supabase
        .from("patients")
        .select("*")
        .eq("id", patientId)
        .single();

      if (patientError) throw patientError;
      setPatient({
        ...patientData,
        name: patientData.name ?? "",
        date_of_birth: patientData.date_of_birth ?? "",
        gender: patientData.gender ?? "",
        phone: patientData.phone ?? "",
        email: patientData.email ?? "",
        address: patientData.address ?? "",
        medical_history: patientData.medical_history ?? "",
      });

      const { data: medsData } = await supabase.from("patient_medications").select("*").eq("patient_id", patientId);
      if (medsData) {
        setMedications(
          medsData.map((med) => ({
            ...med,
            medication_name: med.medication_name ?? "",
            dosage: med.dosage ?? "",
            frequency: med.frequency ?? "",
            started_at: med.started_at ?? "",
          }))
        );
      }

      const { data: allergiesData } = await supabase.from("patient_allergies").select("*").eq("patient_id", patientId);
      if (allergiesData) {
        setAllergies(
          allergiesData.map((allergy) => ({
            ...allergy,
            allergen: allergy.allergen ?? "",
            severity: allergy.severity ?? "mineure",
          }))
        );
      }

      const { data: childrenData } = await supabase
        .from("patient_children")
        .select("*")
        .eq("parent_id", patientId)
        .order("created_at", { ascending: true });
      if (childrenData) setPatientChildren(childrenData as PatientChild[]);

      const { data: consultationsData } = await supabase
        .from("consultations")
        .select(`*, recommendations (*), interaction_alerts (*), sources (*)`)
        .eq("patient_id", patientId)
        .order("created_at", { ascending: false });
      if (consultationsData) setConsultations(consultationsData);

      const { data: vitalityData } = await supabase
        .from("vitality_assessments")
        .select("*")
        .eq("patient_id", patientId)
        .is("child_id", null)
        .order("created_at", { ascending: false });
      if (vitalityData) setVitalityAssessments(vitalityData);

      const { data: scoringData } = await supabase
        .from("vitality_scoring_assessments")
        .select("*")
        .eq("patient_id", patientId)
        .order("created_at", { ascending: false });
      if (scoringData) setScoringAssessments(scoringData);

      const { data: appointmentsData } = await (supabase as any)
        .from("appointments")
        .select("*")
        .eq("patient_id", patientId)
        .order("appointment_date", { ascending: false });
      if (appointmentsData) setAppointments(appointmentsData);

      const { data: sessionReportsData } = await supabase
        .from("session_reports")
        .select("*")
        .eq("patient_id", patientId)
        .order("created_at", { ascending: false });
      if (sessionReportsData) setSessionReports(sessionReportsData);

      const { data: iridologyData } = await supabase
        .from("iridology_sessions")
        .select("*")
        .eq("patient_id", patientId)
        .order("session_date", { ascending: false });
      if (iridologyData) setIridologySessions(iridologyData);
    } catch (error: any) {
      toast.error("Erreur lors du chargement du patient", { description: error.message });
    } finally {
      setLoading(false);
    }
  };

  const savePatient = async () => {
    setSaving(true);
    try {
      let patientId = id;

      // Extract only the fields needed for validation (exclude id)
      const patientDataForValidation = {
        name: patient.name,
        email: patient.email,
        phone: patient.phone,
        address: patient.address,
        medical_history: patient.medical_history,
        date_of_birth: patient.date_of_birth,
        gender: patient.gender,
      };

      const patientValidation = patientSchema.safeParse(patientDataForValidation);
      if (!patientValidation.success) {
        const errors = patientValidation.error.errors.map((e) => e.message).join(", ");
        toast.error("Validation échouée", { description: errors });
        setSaving(false);
        return;
      }

      for (const med of medications) {
        if (med.medication_name) {
          const medValidation = medicationSchema.safeParse({
            medication_name: med.medication_name,
            dosage: med.dosage,
            frequency: med.frequency,
            started_at: med.started_at,
          });
          if (!medValidation.success) {
            toast.error("Validation médicament échouée");
            setSaving(false);
            return;
          }
        }
      }

      for (const allergy of allergies) {
        if (allergy.allergen) {
          const allergyValidation = allergySchema.safeParse({
            allergen: allergy.allergen,
            severity: allergy.severity,
          });
          if (!allergyValidation.success) {
            toast.error("Validation allergie échouée");
            setSaving(false);
            return;
          }
        }
      }

      // Prepare data for database (exclude id for insert/update)
      const patientDataForDb = {
        name: patient.name,
        email: patient.email || null,
        phone: patient.phone || null,
        address: patient.address || null,
        medical_history: patient.medical_history || null,
        date_of_birth: patient.date_of_birth || null,
        gender: patient.gender || null,
      };

      if (id === "new") {
        const { data, error } = await supabase.from("patients").insert([patientDataForDb]).select().single();
        if (error) throw error;
        patientId = data.id;
        setPatient(data);
      } else {
        const { error } = await supabase.from("patients").update(patientDataForDb).eq("id", id);
        if (error) throw error;
      }

      if (patientId) {
        for (const med of medications) {
          const normalized = {
            medication_name: med.medication_name || "",
            dosage: med.dosage || null,
            frequency: med.frequency || null,
            started_at: med.started_at ? med.started_at : null,
          };
          if (med.id) {
            const { error: medErr } = await supabase
              .from("patient_medications")
              .update(normalized)
              .eq("id", med.id);
            if (medErr) throw medErr;
          } else if (med.medication_name) {
            const { error: medErr } = await supabase
              .from("patient_medications")
              .insert([{ ...normalized, patient_id: patientId }]);
            if (medErr) throw medErr;
          }
        }

        for (const allergy of allergies) {
          const normalizedA: any = {
            allergen: allergy.allergen || "",
            severity: (allergy as any).severity || null,
            reaction: (allergy as any).reaction || null,
          };
          if (allergy.id) {
            const { error: aErr } = await supabase
              .from("patient_allergies")
              .update(normalizedA)
              .eq("id", allergy.id);
            if (aErr) throw aErr;
          } else if (allergy.allergen) {
            const { error: aErr } = await supabase
              .from("patient_allergies")
              .insert([{ ...normalizedA, patient_id: patientId }]);
            if (aErr) throw aErr;
          }
        }
      }

      toast.success("Fiche consultant enregistrée");
      clearDraft();
      if (id === "new") {
        navigate(`/patients/${patientId}`);
      }
    } catch (error: any) {
      toast.error("Erreur lors de l'enregistrement", {
        description: (error?.message || "Erreur inconnue") + " — Vos modifications restent sauvegardées localement.",
      });
    } finally {
      setSaving(false);
    }
  };

  // Medication handlers
  const addMedication = () => setMedications([...medications, { medication_name: "", dosage: "", frequency: "", started_at: "" }]);
  const removeMedication = async (index: number) => {
    const med = medications[index];
    if (med.id) await supabase.from("patient_medications").delete().eq("id", med.id);
    setMedications(medications.filter((_, i) => i !== index));
  };
  const updateMedication = (index: number, field: keyof Medication, value: string) => {
    const newMeds = [...medications];
    newMeds[index] = { ...newMeds[index], [field]: value };
    setMedications(newMeds);
  };

  // Allergy handlers
  const addAllergy = () => setAllergies([...allergies, { allergen: "", severity: "mineure" }]);
  const removeAllergy = async (index: number) => {
    const allergy = allergies[index];
    if (allergy.id) await supabase.from("patient_allergies").delete().eq("id", allergy.id);
    setAllergies(allergies.filter((_, i) => i !== index));
  };
  const updateAllergy = (index: number, field: keyof Allergy, value: string) => {
    const newAllergies = [...allergies];
    newAllergies[index] = { ...newAllergies[index], [field]: value };
    setAllergies(newAllergies);
  };

  // Delete handlers
  const deleteVitalityAssessment = async (assessmentId: string) => {
    if (!confirm("Supprimer ce bilan de vitalité ?")) return;
    const { error } = await supabase.from("vitality_assessments").delete().eq("id", assessmentId);
    if (error) return toast.error("Erreur");
    setVitalityAssessments((prev) => prev.filter((v) => v.id !== assessmentId));
    toast.success("Bilan supprimé");
  };

  const deleteSessionReport = async (reportId: string) => {
    if (!confirm("Supprimer ce compte rendu ?")) return;
    const { error } = await supabase.from("session_reports").delete().eq("id", reportId);
    if (error) return toast.error("Erreur");
    setSessionReports((prev) => prev.filter((r) => r.id !== reportId));
    toast.success("Compte rendu supprimé");
  };

  const deleteIridologySession = async (sessionId: string) => {
    if (!confirm("Supprimer cette séance d'iridologie ?")) return;
    const { error } = await supabase.from("iridology_sessions").delete().eq("id", sessionId);
    if (error) return toast.error("Erreur");
    setIridologySessions((prev) => prev.filter((s) => s.id !== sessionId));
    toast.success("Séance supprimée");
  };

  const deleteAppointment = async (aptId: string) => {
    if (!confirm("Supprimer ce rendez-vous ?")) return;
    const { error } = await (supabase as any).from("appointments").delete().eq("id", aptId);
    if (error) return toast.error("Erreur");
    setAppointments((prev) => prev.filter((a) => a.id !== aptId));
    toast.success("Rendez-vous supprimé");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-primary/5 to-secondary/5 flex items-center justify-center">
        <div className="text-primary animate-pulse">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-primary/5 to-secondary/5">
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        {/* Navigation */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => navigate("/dashboard")} size="sm">
              <ArrowLeft className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Accueil</span>
            </Button>
            <Button variant="outline" onClick={() => navigate("/patients")} size="sm">
              <User className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Consultants</span>
            </Button>
          </div>
          <Button onClick={savePatient} disabled={saving} className="gap-2 bg-gradient-to-r from-primary to-primary/80 shadow-lg">
            <Save className="w-4 h-4" />
            {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </div>

        {/* Patient Header */}
        {id && id !== "new" && (
          <div className="mb-6">
            <PatientHeader
              patient={patient}
              medicationsCount={medications.length}
              allergiesCount={allergies.length}
              childrenCount={patientChildren.length}
              appointmentsCount={appointments.length}
            />
          </div>
        )}

        {/* Quick Actions */}
        {id && id !== "new" && (
          <div className="mb-6">
            <PatientQuickActions patientId={id} />
          </div>
        )}

        {/* Main Content Tabs */}
        <Tabs defaultValue="info" className="space-y-6">
          <div className="overflow-x-auto -mx-4 px-4 pb-2">
            <TabsList className="inline-flex w-max sm:grid sm:w-full sm:grid-cols-4 h-auto p-1 gap-1 bg-white/50 dark:bg-card/50 backdrop-blur-sm shadow-sm">
              <TabsTrigger value="info" className="gap-2 text-xs sm:text-sm px-3 py-2">
                <User className="w-4 h-4" />
                <span className="hidden sm:inline">Infos</span>
              </TabsTrigger>
              <TabsTrigger value="health" className="gap-2 text-xs sm:text-sm px-3 py-2">
                <Pill className="w-4 h-4" />
                <span className="hidden sm:inline">Santé</span>
              </TabsTrigger>
              <TabsTrigger value="sessions" className="gap-2 text-xs sm:text-sm px-3 py-2">
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">Séances / Bilans</span>
              </TabsTrigger>
              <TabsTrigger value="appointments" className="gap-2 text-xs sm:text-sm px-3 py-2">
                <Calendar className="w-4 h-4" />
                <span className="hidden sm:inline">RDV</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Tab: Info */}
          <TabsContent value="info" className="space-y-6">
            <PatientInfoForm patient={patient} setPatient={setPatient} />
            {id && id !== "new" && (
              <ChildrenSection patientId={id} children={patientChildren} onChildrenChange={setPatientChildren} />
            )}
            {id && id !== "new" && (
              <QuestionnairesSection patientId={id} patientName={patient.name} patientEmail={patient.email} patientPhone={patient.phone} />
            )}
          </TabsContent>

          {/* Tab: Health */}
          <TabsContent value="health" className="space-y-6">
            <MedicationsCard medications={medications} onAdd={addMedication} onRemove={removeMedication} onUpdate={updateMedication} />
            <AllergiesCard allergies={allergies} onAdd={addAllergy} onRemove={removeAllergy} onUpdate={updateAllergy} />
          </TabsContent>

          {/* Tab: Sessions / Bilans */}
          <TabsContent value="sessions" className="space-y-6">
            {/* Vitality Assessments */}
            <Card className="shadow-md border-0 bg-white/80 dark:bg-card backdrop-blur-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Bilans de vitalité</CardTitle>
                      <CardDescription>{vitalityAssessments.length} bilan(s)</CardDescription>
                    </div>
                  </div>
                  {id && (
                    <Button variant="outline" size="sm" onClick={() => navigate(`/vitality?patientId=${id}`)}>
                      <Plus className="w-4 h-4 mr-1" />
                      Nouveau
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {vitalityAssessments.length === 0 ? (
                  <div className="text-center py-8 bg-muted/30 rounded-xl">
                    <Sparkles className="w-10 h-10 mx-auto text-muted-foreground/40 mb-2" />
                    <p className="text-muted-foreground text-sm">Aucun bilan enregistré</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {vitalityAssessments.map((v) => (
                      <div key={v.id} className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 rounded-xl border border-amber-100 dark:border-amber-900">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium">{new Date(v.created_at).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" })}</p>
                            {v.consultation_reason && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{v.consultation_reason}</p>}
                            <div className="flex gap-2 mt-2">
                              {v.energy_level && <Badge variant="outline">Énergie: {v.energy_level}/10</Badge>}
                              {v.temperament && <Badge variant="secondary">{v.temperament}</Badge>}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/vitality?patientId=${id}&assessmentId=${v.id}`)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={async () => {
                              const { generateVitalityAssessmentPDF } = await import("@/utils/pdfExport");
                              generateVitalityAssessmentPDF(v, patient.name);
                            }}>
                              <FileDown className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => deleteVitalityAssessment(v.id)} className="text-destructive">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Vitality Scoring Assessments */}
            <Card className="shadow-md border-0 bg-white/80 dark:bg-card backdrop-blur-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Bilan vitalité — Scoring</CardTitle>
                      <CardDescription>{scoringAssessments.length} bilan(s)</CardDescription>
                    </div>
                  </div>
                  {id && (
                    <Button variant="outline" size="sm" onClick={() => navigate(`/vitality-scoring?patientId=${id}`)}>
                      <Plus className="w-4 h-4 mr-1" />
                      Nouveau
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {scoringAssessments.length === 0 ? (
                  <div className="text-center py-8 bg-muted/30 rounded-xl">
                    <Sparkles className="w-10 h-10 mx-auto text-muted-foreground/40 mb-2" />
                    <p className="text-muted-foreground text-sm">Aucun bilan de scoring enregistré</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {scoringAssessments.map((s) => {
                      const total = s.scores?.total ?? 0;
                      return (
                        <div key={s.id} className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950/20 dark:to-purple-950/20 rounded-xl border border-indigo-100 dark:border-indigo-900">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium">{new Date(s.assessment_date || s.created_at).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" })}</p>
                              <div className="flex gap-2 mt-2 flex-wrap">
                                <Badge variant="outline">Score global : {total}/120</Badge>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => navigate(`/vitality-scoring?patientId=${id}&assessmentId=${s.id}`)}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={async () => {
                                const { generateVitalityScoringPDF } = await import("@/utils/vitalityScoringPdfExport");
                                generateVitalityScoringPDF(s, { name: patient.name, date_of_birth: patient.date_of_birth, gender: patient.gender });
                              }}>
                                <FileDown className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={async () => {
                                if (!confirm("Supprimer ce bilan ?")) return;
                                await supabase.from("vitality_scoring_assessments").delete().eq("id", s.id);
                                setScoringAssessments((prev) => prev.filter((x) => x.id !== s.id));
                              }} className="text-destructive">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Session Reports */}
            <Card className="shadow-md border-0 bg-white/80 dark:bg-card backdrop-blur-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Comptes rendus</CardTitle>
                      <CardDescription>{sessionReports.length} compte(s) rendu(s)</CardDescription>
                    </div>
                  </div>
                  {id && (
                    <Button variant="outline" size="sm" onClick={() => navigate(`/session-report?patientId=${id}`)}>
                      <Plus className="w-4 h-4 mr-1" />
                      Nouveau
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {sessionReports.length === 0 ? (
                  <div className="text-center py-8 bg-muted/30 rounded-xl">
                    <FileText className="w-10 h-10 mx-auto text-muted-foreground/40 mb-2" />
                    <p className="text-muted-foreground text-sm">Aucun compte rendu</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {sessionReports.map((r) => (
                      <div key={r.id} className="p-4 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-950/20 dark:to-green-950/20 rounded-xl border border-emerald-100 dark:border-emerald-900 flex items-center justify-between">
                        <div>
                          <p className="font-medium">{r.accompaniment_title}</p>
                          <p className="text-sm text-muted-foreground">{new Date(r.report_date).toLocaleDateString("fr-FR")}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setSelectedSessionReport(r)} title="Aperçu rapide">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={async () => {
                            const { downloadSessionReportPDF } = await import("@/utils/sessionReportPdfExport");
                            downloadSessionReportPDF(r, patient.name);
                          }} title="Télécharger PDF">
                            <FileDown className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteSessionReport(r.id)} className="text-destructive">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Iridology Sessions */}
            <Card className="shadow-md border-0 bg-white/80 dark:bg-card backdrop-blur-sm border-2 border-primary/20">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Eye className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg text-primary">Iridologie</CardTitle>
                      <CardDescription>{iridologySessions.length} séance(s)</CardDescription>
                    </div>
                  </div>
                  {id && (
                    <Button variant="outline" size="sm" onClick={() => navigate(`/iridology/session/new?patientId=${id}`)}>
                      <Plus className="w-4 h-4 mr-1" />
                      Nouvelle
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {iridologySessions.length === 0 ? (
                  <div className="text-center py-8 bg-muted/30 rounded-xl">
                    <Eye className="w-10 h-10 mx-auto text-muted-foreground/40 mb-2" />
                    <p className="text-muted-foreground text-sm">Aucune séance d'iridologie</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {iridologySessions.map((s) => (
                      <div key={s.id} className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 rounded-xl border border-blue-100 dark:border-blue-900">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium">{new Date(s.session_date).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" })}</p>
                            <div className="flex gap-2 mt-2">
                              <Badge variant="secondary">
                                {s.eye_observed === "right" ? "Œil droit" : s.eye_observed === "left" ? "Œil gauche" : "Les deux"}
                              </Badge>
                              {s.vitality_level && (
                                <Badge variant={s.vitality_level === "good" ? "default" : "outline"}>
                                  {s.vitality_level === "good" ? "Bonne vitalité" : s.vitality_level === "medium" ? "Moyenne" : "À soutenir"}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => setSelectedIridology(s)} title="Aperçu rapide">
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/iridology/${s.id}`)} title="Voir la séance complète">
                              <FileText className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => deleteIridologySession(s.id)} className="text-destructive">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Appointments */}
          <TabsContent value="appointments" className="space-y-6">
            <Card className="shadow-md border-0 bg-white/80 dark:bg-card backdrop-blur-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Rendez-vous</CardTitle>
                      <CardDescription>{appointments.length} rendez-vous</CardDescription>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => navigate("/appointments")}>
                    <Calendar className="w-4 h-4 mr-1" />
                    Agenda
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {appointments.length === 0 ? (
                  <div className="text-center py-8 bg-muted/30 rounded-xl">
                    <Calendar className="w-10 h-10 mx-auto text-muted-foreground/40 mb-2" />
                    <p className="text-muted-foreground text-sm">Aucun rendez-vous</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {appointments.map((apt) => {
                      const aptDate = new Date(apt.appointment_date);
                      const isPast = aptDate < new Date();
                      return (
                        <div
                          key={apt.id}
                          className={`p-4 rounded-xl border ${
                            apt.status === "scheduled" && !isPast
                              ? "bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20 border-purple-100"
                              : "bg-muted/30 border-muted"
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <Clock className="w-5 h-5 mt-0.5 text-purple-600" />
                              <div>
                                <p className="font-medium">
                                  {aptDate.toLocaleDateString("fr-FR", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  {aptDate.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} - {apt.duration_minutes} min
                                </p>
                                {apt.notes && <p className="text-sm text-muted-foreground mt-1">{apt.notes}</p>}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant={apt.status === "scheduled" ? "default" : apt.status === "completed" ? "secondary" : "outline"}>
                                {apt.status === "scheduled" ? "Planifié" : apt.status === "completed" ? "Complété" : apt.status === "cancelled" ? "Annulé" : apt.status}
                              </Badge>
                              <Button variant="ghost" size="sm" onClick={() => deleteAppointment(apt.id)} className="text-destructive">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Quick View Dialogs */}
        {/* Vitality Assessment Quick View */}
        <Dialog open={!!selectedVitality} onOpenChange={() => setSelectedVitality(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-600" />
                Bilan de vitalité
              </DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh] pr-4">
              {selectedVitality && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground">Date</p>
                      <p className="font-medium">{new Date(selectedVitality.created_at).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" })}</p>
                    </div>
                    {selectedVitality.energy_level && (
                      <div className="p-3 bg-muted/50 rounded-lg">
                        <p className="text-sm text-muted-foreground">Niveau d'énergie</p>
                        <p className="font-medium">{selectedVitality.energy_level}/10</p>
                      </div>
                    )}
                    {selectedVitality.temperament && (
                      <div className="p-3 bg-muted/50 rounded-lg">
                        <p className="text-sm text-muted-foreground">Tempérament</p>
                        <p className="font-medium">{selectedVitality.temperament}</p>
                      </div>
                    )}
                    {selectedVitality.constitution && (
                      <div className="p-3 bg-muted/50 rounded-lg">
                        <p className="text-sm text-muted-foreground">Constitution</p>
                        <p className="font-medium">{selectedVitality.constitution}</p>
                      </div>
                    )}
                  </div>
                  {selectedVitality.consultation_reason && (
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">Motif de consultation</p>
                      <p className="text-sm">{selectedVitality.consultation_reason}</p>
                    </div>
                  )}
                  {selectedVitality.priorities && (
                    <div className="p-3 bg-primary/5 rounded-lg border-l-4 border-primary">
                      <p className="text-sm text-muted-foreground mb-1">Priorités</p>
                      <p className="text-sm">{selectedVitality.priorities}</p>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => navigate(`/vitality?patientId=${id}&assessmentId=${selectedVitality?.id}`)}>
                Voir complet
              </Button>
              <Button onClick={async () => {
                if (selectedVitality) {
                  const { generateVitalityAssessmentPDF } = await import("@/utils/pdfExport");
                  generateVitalityAssessmentPDF(selectedVitality, patient.name);
                }
              }}>
                <FileDown className="w-4 h-4 mr-2" />
                Télécharger PDF
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Consultation Quick View */}
        <Dialog open={!!selectedConsultation} onOpenChange={() => setSelectedConsultation(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Leaf className="w-5 h-5 text-green-600" />
                Analyse naturopathique
              </DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh] pr-4">
              {selectedConsultation && (
                <div className="space-y-4">
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Date</p>
                    <p className="font-medium">{new Date(selectedConsultation.created_at).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" })}</p>
                  </div>
                  {selectedConsultation.symptoms_text && (
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">Symptômes</p>
                      <p className="text-sm">{selectedConsultation.symptoms_text}</p>
                    </div>
                  )}
                  {selectedConsultation.recommendations?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Recommandations ({selectedConsultation.recommendations.length})</p>
                      <div className="space-y-2">
                        {selectedConsultation.recommendations.map((r: any) => (
                          <div key={r.id} className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
                            <p className="font-medium text-sm">{r.title}</p>
                            {r.detail && <p className="text-sm text-muted-foreground mt-1">{r.detail}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
          </DialogContent>
        </Dialog>

        {/* Session Report Quick View */}
        <Dialog open={!!selectedSessionReport} onOpenChange={() => setSelectedSessionReport(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" />
                Compte rendu de séance
              </DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh] pr-4">
              {selectedSessionReport && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground">Accompagnement</p>
                      <p className="font-medium">{selectedSessionReport.accompaniment_title}</p>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground">Date</p>
                      <p className="font-medium">{new Date(selectedSessionReport.report_date).toLocaleDateString("fr-FR")}</p>
                    </div>
                  </div>
                  {selectedSessionReport.hygiene_vie?.length > 0 && (
                    <div className="p-3 bg-primary/5 rounded-lg">
                      <p className="text-sm font-medium mb-2">Hygiène de vie</p>
                      <ul className="text-sm space-y-1">
                        {selectedSessionReport.hygiene_vie.slice(0, 3).map((item: any, i: number) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="text-primary">•</span>
                            <span className="line-clamp-2">{item.content}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {selectedSessionReport.soutiens_naturels?.length > 0 && (
                    <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
                      <p className="text-sm font-medium mb-2">Soutiens naturels ({selectedSessionReport.soutiens_naturels.length})</p>
                      <div className="flex flex-wrap gap-1">
                        {selectedSessionReport.soutiens_naturels.slice(0, 5).map((item: any, i: number) => (
                          <Badge key={i} variant="outline" className="text-xs">{item.content?.split('\n')[0]}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => {
                setSelectedSessionReport(null);
                navigate(`/session-report?patientId=${id}&reportId=${selectedSessionReport?.id}`);
              }}>
                Éditer
              </Button>
              <Button onClick={async () => {
                if (selectedSessionReport) {
                  const { downloadSessionReportPDF } = await import("@/utils/sessionReportPdfExport");
                  downloadSessionReportPDF(selectedSessionReport, patient.name);
                }
              }}>
                <FileDown className="w-4 h-4 mr-2" />
                Télécharger PDF
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Iridology Quick View */}
        <Dialog open={!!selectedIridology} onOpenChange={() => setSelectedIridology(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-blue-600" />
                Séance d'iridologie
              </DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh] pr-4">
              {selectedIridology && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground">Date</p>
                      <p className="font-medium">{new Date(selectedIridology.session_date).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" })}</p>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground">Œil observé</p>
                      <p className="font-medium">{selectedIridology.eye_observed === "right" ? "Œil droit" : selectedIridology.eye_observed === "left" ? "Œil gauche" : "Les deux yeux"}</p>
                    </div>
                    {selectedIridology.vitality_level && (
                      <div className="p-3 bg-muted/50 rounded-lg">
                        <p className="text-sm text-muted-foreground">Niveau de vitalité</p>
                        <p className="font-medium">{selectedIridology.vitality_level === "good" ? "Bonne" : selectedIridology.vitality_level === "medium" ? "Moyenne" : "À soutenir"}</p>
                      </div>
                    )}
                    {selectedIridology.consultation_type && (
                      <div className="p-3 bg-muted/50 rounded-lg">
                        <p className="text-sm text-muted-foreground">Type</p>
                        <p className="font-medium">{selectedIridology.consultation_type}</p>
                      </div>
                    )}
                  </div>
                  {selectedIridology.additional_observations && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">Observations</p>
                      <p className="text-sm">{selectedIridology.additional_observations}</p>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {selectedIridology.terrain_nervous && <Badge variant="secondary">Terrain nerveux</Badge>}
                    {selectedIridology.terrain_digestive && <Badge variant="secondary">Terrain digestif</Badge>}
                    {selectedIridology.terrain_circulatory && <Badge variant="secondary">Terrain circulatoire</Badge>}
                    {selectedIridology.terrain_eliminatory && <Badge variant="secondary">Terrain éliminatoire</Badge>}
                  </div>
                </div>
              )}
            </ScrollArea>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => {
                setSelectedIridology(null);
                navigate(`/iridology/${selectedIridology?.id}`);
              }}>
                Voir complet
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
